import { call, put, takeLatest } from "redux-saga/effects";
import {
  CREATE_EDM_DATASET,
  CreateEdmDatasetFailed,
  CreateEdmDatasetSuccess,
  GET_EDM_ARTWORK,
  GET_EDM_ARTWORKS,
  GET_EDM_DATASET,
  GET_EDM_DATASET_BLOCKED,
  GET_EDM_DATASET_HANDOFF_PACKAGE,
  GET_EDM_POLICY,
  GET_EDM_DATASET_SUMMARY,
  GET_EDM_DATASETS,
  GetEdmArtworkFailed,
  GetEdmArtworkSuccess,
  GetEdmArtworksFailed,
  GetEdmArtworksSuccess,
  GetEdmDatasetBlockedFailed,
  GetEdmDatasetBlockedSuccess,
  GetEdmDatasetFailed,
  GetEdmDatasetHandoffPackageFailed,
  GetEdmDatasetHandoffPackageSuccess,
  GetEdmPolicyFailed,
  GetEdmPolicySuccess,
  GetEdmDatasetSuccess,
  GetEdmDatasetsFailed,
  GetEdmDatasetsSuccess,
  GetEdmDatasetSummaryFailed,
  GetEdmDatasetSummarySuccess,
  RECOMPUTE_EDM_ARTWORK,
  RECOMPUTE_EDM_DATASET,
  RecomputeEdmArtworkFailed,
  RecomputeEdmArtworkSuccess,
  RecomputeEdmDatasetFailed,
  RecomputeEdmDatasetSuccess,
  TRANSITION_EDM_ARTWORK,
  TransitionEdmArtworkFailed,
  TransitionEdmArtworkSuccess,
  UPDATE_EDM_ARTWORK_PROFILE,
  UPDATE_EDM_DATASET,
  UpdateEdmArtworkProfileFailed,
  UpdateEdmArtworkProfileSuccess,
  UpdateEdmDatasetFailed,
  UpdateEdmDatasetSuccess,
  VALIDATE_EDM_ARTWORK_MEDIA,
  VALIDATE_EDM_DATASET_MEDIA,
  ValidateEdmArtworkMediaFailed,
  ValidateEdmArtworkMediaSuccess,
  ValidateEdmDatasetMediaFailed,
  ValidateEdmDatasetMediaSuccess,
} from "./admin-edm.actions";
import {
  createEdmDatasetRequest,
  getEdmArtworkRequest,
  getEdmArtworksRequest,
  getEdmDatasetBlockedRequest,
  getEdmDatasetHandoffPackageRequest,
  getEdmDatasetRequest,
  getEdmDatasetsRequest,
  getEdmDatasetSummaryRequest,
  getEdmPolicyRequest,
  recomputeEdmArtworkRequest,
  recomputeEdmDatasetRequest,
  transitionEdmArtworkRequest,
  updateEdmArtworkProfileRequest,
  updateEdmDatasetRequest,
  validateEdmArtworkMediaRequest,
  validateEdmDatasetMediaRequest,
} from "./admin-edm.api";
import { formatAdminEdmUiError } from "../admin-edm.errors";

const getErrorMessage = (operation: Parameters<typeof formatAdminEdmUiError>[0], data: any) =>
  formatAdminEdmUiError(operation, data).message;

const getErrorPayload = (operation: Parameters<typeof formatAdminEdmUiError>[0], data: any) => {
  const normalized = formatAdminEdmUiError(operation, data);
  return {
    message: normalized.message,
    details: normalized.details || null,
  };
};

function* getDatasets(action: any) {
  const data = yield call(getEdmDatasetsRequest, action.payload || {});
  if (data.status) {
    yield put(GetEdmDatasetsSuccess(data.data));
  } else {
    yield put(GetEdmDatasetsFailed(getErrorMessage("load-datasets", data)));
  }
}

function* createDataset(action: any) {
  const { payload, onSuccess } = action.payload || {};
  const data = yield call(createEdmDatasetRequest, payload);
  if (data.status) {
    yield put(CreateEdmDatasetSuccess(data.data));
    if (typeof onSuccess === 'function') onSuccess(data.data);
  } else {
    yield put(CreateEdmDatasetFailed(getErrorMessage("create-dataset", data)));
  }
}

function* getDataset(action: any) {
  const data = yield call(getEdmDatasetRequest, action.payload.datasetKey);
  if (data.status) {
    yield put(GetEdmDatasetSuccess(data.data));
  } else {
    yield put(GetEdmDatasetFailed(getErrorMessage("load-dataset", data)));
  }
}

function* updateDataset(action: any) {
  const { datasetKey, payload, onSuccess } = action.payload;
  const data = yield call(updateEdmDatasetRequest, datasetKey, payload);
  if (data.status) {
    yield put(UpdateEdmDatasetSuccess(data.data));
    if (typeof onSuccess === 'function') onSuccess(data.data);
  } else {
    yield put(UpdateEdmDatasetFailed(getErrorMessage("save-dataset", data)));
  }
}

function* recomputeDataset(action: any) {
  const { datasetKey, payload, onSuccess } = action.payload;
  const data = yield call(recomputeEdmDatasetRequest, datasetKey, payload);
  if (data.status) {
    yield put(RecomputeEdmDatasetSuccess(data.data));
    if (typeof onSuccess === 'function') onSuccess(data.data);
  } else {
    yield put(RecomputeEdmDatasetFailed(getErrorMessage("recompute-dataset", data)));
  }
}

function* getDatasetSummary(action: any) {
  const { datasetKey, profileId } = action.payload;
  const data = yield call(getEdmDatasetSummaryRequest, datasetKey, profileId);
  if (data.status) {
    yield put(GetEdmDatasetSummarySuccess(data.data));
  } else {
    yield put(GetEdmDatasetSummaryFailed(getErrorMessage("load-dataset-summary", data)));
  }
}

function* getDatasetBlocked(action: any) {
  const { datasetKey, profileId } = action.payload;
  const data = yield call(getEdmDatasetBlockedRequest, datasetKey, profileId);
  if (data.status) {
    yield put(GetEdmDatasetBlockedSuccess(data.data));
  } else {
    yield put(GetEdmDatasetBlockedFailed(getErrorMessage("load-dataset-blocked", data)));
  }
}

function* validateDatasetMedia(action: any) {
  const { datasetKey, payload, onSuccess } = action.payload;
  const data = yield call(validateEdmDatasetMediaRequest, datasetKey, payload);
  if (data.status) {
    yield put(ValidateEdmDatasetMediaSuccess(data.data));
    if (typeof onSuccess === 'function') onSuccess(data.data);
  } else {
    yield put(ValidateEdmDatasetMediaFailed(getErrorMessage("validate-dataset-media", data)));
  }
}

function* getDatasetHandoffPackage(action: any) {
  const { datasetKey, profileId, onSuccess } = action.payload;
  const data = yield call(getEdmDatasetHandoffPackageRequest, datasetKey, profileId);
  if (data.status) {
    yield put(GetEdmDatasetHandoffPackageSuccess(data.data));
    if (typeof onSuccess === 'function') onSuccess(data.data);
  } else {
    yield put(GetEdmDatasetHandoffPackageFailed(getErrorMessage("generate-dataset-handoff", data)));
  }
}

function* getPolicy() {
  const data = yield call(getEdmPolicyRequest);
  if (data.status) {
    yield put(GetEdmPolicySuccess(data.data));
  } else {
    yield put(GetEdmPolicyFailed(getErrorMessage("load-policy", data)));
  }
}

function* getArtworks(action: any) {
  const data = yield call(getEdmArtworksRequest, action.payload || {});
  if (data.status) {
    yield put(GetEdmArtworksSuccess(data.data));
  } else {
    yield put(GetEdmArtworksFailed(getErrorMessage("load-artworks", data)));
  }
}

function* getArtwork(action: any) {
  const data = yield call(getEdmArtworkRequest, action.payload.artworkId);
  if (data.status) {
    yield put(GetEdmArtworkSuccess(data.data));
  } else {
    yield put(GetEdmArtworkFailed(getErrorMessage("load-artwork", data)));
  }
}

function* updateArtworkProfile(action: any) {
  const { artworkId, payload, profileId, onSuccess } = action.payload;
  const data = yield call(updateEdmArtworkProfileRequest, artworkId, payload, profileId);
  if (data.status) {
    yield put(UpdateEdmArtworkProfileSuccess(data.data));
    if (typeof onSuccess === 'function') onSuccess(data.data);
  } else {
    yield put(UpdateEdmArtworkProfileFailed(getErrorPayload("save-artwork-profile", data)));
  }
}

function* recomputeArtwork(action: any) {
  const { artworkId, payload, onSuccess } = action.payload;
  const data = yield call(recomputeEdmArtworkRequest, artworkId, payload);
  if (data.status) {
    yield put(RecomputeEdmArtworkSuccess(data.data));
    if (typeof onSuccess === 'function') onSuccess(data.data);
  } else {
    yield put(RecomputeEdmArtworkFailed(getErrorMessage("recompute-artwork", data)));
  }
}

function* transitionArtwork(action: any) {
  const { artworkId, payload, onSuccess } = action.payload;
  const data = yield call(transitionEdmArtworkRequest, artworkId, payload);
  if (data.status) {
    yield put(TransitionEdmArtworkSuccess(data.data));
    if (typeof onSuccess === 'function') onSuccess(data.data);
  } else {
    yield put(TransitionEdmArtworkFailed(getErrorMessage("transition-artwork", data)));
  }
}

function* validateArtworkMedia(action: any) {
  const { artworkId, payload, onSuccess } = action.payload;
  const data = yield call(validateEdmArtworkMediaRequest, artworkId, payload);
  if (data.status) {
    yield put(ValidateEdmArtworkMediaSuccess(data.data));
    if (typeof onSuccess === 'function') onSuccess(data.data);
  } else {
    yield put(ValidateEdmArtworkMediaFailed(getErrorMessage("validate-artwork-media", data)));
  }
}

export default function* watchAdminEdmActions() {
  yield takeLatest(GET_EDM_DATASETS, getDatasets);
  yield takeLatest(CREATE_EDM_DATASET, createDataset);
  yield takeLatest(GET_EDM_DATASET, getDataset);
  yield takeLatest(UPDATE_EDM_DATASET, updateDataset);
  yield takeLatest(RECOMPUTE_EDM_DATASET, recomputeDataset);
  yield takeLatest(GET_EDM_DATASET_SUMMARY, getDatasetSummary);
  yield takeLatest(GET_EDM_DATASET_BLOCKED, getDatasetBlocked);
  yield takeLatest(VALIDATE_EDM_DATASET_MEDIA, validateDatasetMedia);
    yield takeLatest(GET_EDM_DATASET_HANDOFF_PACKAGE, getDatasetHandoffPackage);
    yield takeLatest(GET_EDM_POLICY, getPolicy);
    yield takeLatest(GET_EDM_ARTWORKS, getArtworks);
  yield takeLatest(GET_EDM_ARTWORK, getArtwork);
  yield takeLatest(UPDATE_EDM_ARTWORK_PROFILE, updateArtworkProfile);
  yield takeLatest(RECOMPUTE_EDM_ARTWORK, recomputeArtwork);
  yield takeLatest(TRANSITION_EDM_ARTWORK, transitionArtwork);
  yield takeLatest(VALIDATE_EDM_ARTWORK_MEDIA, validateArtworkMedia);
}
